(ns shared.core.components.recharts.area-chart
  (:require [shared.core.components.recharts.tooltip :as tooltip]
            [shared.core.components.recharts.util :as recharts.util]
            [shared.core.style :as style]
            [shared.core.util :as util]))

(defn- gradient-id [base-id key-name]
  (str "area-gradient-" base-id (when key-name (str "-" (util/->kebab-case-string key-name)))))

(defn- gradient-def
  "Renders a linearGradient def for a single area series."
  [{:keys [id hex-color fill-opacity]}]
  [:linearGradient {:id id :x1 "0%" :y1 "0%" :x2 "0%" :y2 "100%"}
   [:stop {:offset 0 :stop-color hex-color :stop-opacity fill-opacity}]
   [:stop {:offset 0.694137 :stop-color hex-color :stop-opacity 0}]])

(defn area-chart
  [{:keys [id
           data
           x-key
           y-key
           y-keys
           series-key
           data-key
           y-domain
           color
           colors
           average
           highlights
           smooth?
           fill-opacity
           stack-id
           show-legend?
           legend-label-fn
           legend-config
           x-axis-config
           x-axis-visible?
           y-axis-visible?
           x-grid-visible?
           y-axis-config
           y-grid-visible?
           tooltip-visible?
           tooltip-label-fn
           dynamic-margins?
           goal
           goal-config
           width
           height]
    :or {width "100%"
         height 90
         highlights []
         smooth? true
         fill-opacity 0.6
         color (get-in style/colors [:blue :shade-1])
         x-axis-visible? true
         y-axis-visible? false
         x-grid-visible? false
         y-grid-visible? true
         tooltip-visible? true
         dynamic-margins? true}}]
  (let [effective-id (or id (str (hash data)))
        [data y-keys] (recharts.util/pivot-series {:data data
                                                   :y-keys y-keys
                                                   :series-key series-key
                                                   :x-key x-key
                                                   :value-key (or y-key data-key "value")})
        y-vals (if y-keys
                 (mapcat (fn [k] (map #(get % k) data)) y-keys)
                 (map #(get % y-key) data))
        max-value (or (recharts.util/axis-max y-axis-config)
                      (->> y-vals (filter number?) (reduce max (or goal 0))))
        goal-label (when goal
                     (recharts.util/goal-label goal goal-config (:percent? y-axis-config)))
        y-axis-width (when (and dynamic-margins? y-axis-visible? (not (:width y-axis-config)))
                       (recharts.util/fit-y-axis-width [(recharts.util/tick-label y-axis-config max-value)]))
        x-axis-height (when (and dynamic-margins? x-axis-visible? (not (:height x-axis-config)))
                        (recharts.util/fit-x-axis-height
                          (map #(or (get % x-key) (get % (keyword x-key))) data)
                          (:angle x-axis-config)))
        line-type (if smooth? "monotone" "linear")
        x->y (when (and x-key y-key)
               (into {} (map #(vector (get % (keyword x-key))
                                      (get % (keyword y-key)))
                             data)))
        key->color (fn [k]
                     (or (when colors (get colors k))
                         (recharts.util/color-from-string (name k))))
        key->hex (fn [k]
                   (or (when colors (get colors k))
                       (util/str->hex (name k))))
        area-elements (if y-keys
                        (mapv (fn [k]
                                (let [k-color (key->color k)
                                      grad-id (gradient-id effective-id (name k))]
                                  [recharts.util/re-area {:type line-type
                                                          :dataKey k
                                                          :stroke k-color
                                                          :fill (str "url(#" grad-id ")")
                                                          :stackId stack-id
                                                          :dot false}]))
                              y-keys)
                        [[recharts.util/re-area {:type line-type
                                                 :dataKey y-key
                                                 :stroke color
                                                 :fill (str "url(#" (gradient-id effective-id nil) ")")
                                                 :dot false}]])
        highlight-elements (mapcat (fn [{:keys [x important?]}]
                                     (let [h-color (get-in style/colors (if important? [:red :shade-6] [:orange :shade-2]))]
                                       [[recharts.util/re-reference-line
                                         {:segment [{:x x :y 0}
                                                    {:x x :y (get x->y x)}]
                                          :stroke h-color
                                          :stroke-width 1
                                          :stroke-dasharray "6 4"}]
                                        [recharts.util/re-reference-dot
                                         {:x x
                                          :y (get x->y x)
                                          :r 4
                                          :fill h-color
                                          :stroke (style/alc-color "Background/card_hex")
                                          :filter (str "url(#area-dot-shadow-" effective-id ")")}]]))
                                   highlights)
        avg-element (when average
                      [recharts.util/re-reference-line
                       {:y (:value average)
                        :stroke (style/alc-color "Text/primary")
                        :stroke-dasharray "6 4"
                        :label {:value (:label average)
                                :position "insideBottomLeft"
                                :fill (style/alc-color "Text/primary")
                                :font-family style/roboto-font-family
                                :font-size 7.6}}])
        legend-entries (when (and show-legend? y-keys)
                         (mapv (fn [k]
                                 {:label (if legend-label-fn (legend-label-fn k) (name k))
                                  :color (key->color k)})
                               y-keys))]
    [:div {:style {:width width
                   :display :flex
                   :flex-direction :column
                   :position :relative}}
     [recharts.util/re-responsive-container {:width "100%" :height height}
      (into [recharts.util/re-area-chart (cond-> {:data data}
                                                 goal
                                                 (assoc :margin {:top 5
                                                                 :right (recharts.util/goal-right-margin goal-label)
                                                                 :bottom 5
                                                                 :left 5}))
             [:defs
              (if y-keys
                (for [k y-keys]
                  [gradient-def {:id (gradient-id effective-id (name k))
                                 :hex-color (key->hex k)
                                 :fill-opacity fill-opacity}])
                [gradient-def {:id (gradient-id effective-id nil)
                               :hex-color (or color (when y-key (key->hex y-key)))
                               :fill-opacity fill-opacity}])
              [:filter {:id (str "area-dot-shadow-" effective-id)
                        :x "-50%"
                        :y "-20%"
                        :width "200%"
                        :height "200%"}
               [:feDropShadow {:dx 0
                               :dy 4
                               :std-deviation 2
                               :flood-color "#000"
                               :flood-opacity "0.25"}]]]
             [recharts.util/re-x-axis (merge {:height (if x-axis-visible? (or x-axis-height 12) 0)
                                              :dataKey x-key
                                              :tickLine false
                                              :interval "preserveStartEnd"
                                              :tick #(recharts.util/custom-rotated-x-tick % x-axis-config)}
                                             (recharts.util/strip-axis-options x-axis-config))]
             [recharts.util/re-y-axis (cond-> {:width (if y-axis-visible? (or y-axis-width 40) 0)
                                               :tickLine false
                                               :axisLine false
                                               :tick #(recharts.util/custom-y-tick % (select-keys y-axis-config [:percent?]))}
                                              y-domain
                                              (assoc :domain y-domain)
                                              y-axis-config
                                              (merge (recharts.util/axis-props y-axis-config)))]
             [recharts.util/re-cartesian-grid {:stroke (style/alc-color "lines/chart_grid")
                                               :vertical x-grid-visible?
                                               :horizontal y-grid-visible?}]
             (when tooltip-visible?
               [recharts.util/re-tooltip
                {:content (tooltip/chart-tooltip-content
                            {:row-fn (when tooltip-label-fn
                                       (fn [r] (assoc r :label (tooltip-label-fn r))))})}])]
            (concat area-elements
                    highlight-elements
                    (when avg-element [avg-element])
                    (when goal
                      [(recharts.util/goal-line {:goal goal
                                                 :goal-config goal-config
                                                 :label goal-label})])))]
     (when (not-empty legend-entries)
       [recharts.util/standalone-legend (assoc legend-config :entries legend-entries)])]))